/**
 * Contract for the automatic passkey-provider detach handshake between the web
 * app and the browser extension.
 *
 * The extension's `chrome.webAuthenticationProxy` passkey provider is
 * all-or-nothing: while attached it intercepts every WebAuthn call in the
 * profile and returns no PRF, which shadows the platform authenticator on
 * `heybesafe.com` and breaks the web app's biometric-unlock PRF ceremony. To
 * fix this the web app briefly asks the extension to detach the provider around
 * a PRF ceremony, then resume it.
 *
 * Message flow (all `window.postMessage`, own-origin only):
 *   web app  → { source: HBS_WEB_MESSAGE_SOURCE, type: SUSPEND }  → content script
 *   content script → SW SUSPEND_PASSKEY_PROVIDER → detach() → { wasAttached }
 *   content script → { source: HBS_EXT_MESSAGE_SOURCE, type: SUSPENDED, wasAttached } → web app
 *   web app runs the create()/get() PRF ceremony (reaches the native authenticator)
 *   web app  → { source: HBS_WEB_MESSAGE_SOURCE, type: RESUME }   → content script
 *   content script → SW RESUME_PASSKEY_PROVIDER → attach() → { source: EXT, type: RESUMED }
 *
 * The web-app side treats a missing reply within a short timeout as "no
 * extension / provider off" and runs the ceremony unmodified. See
 * docs/superpowers/specs/2026-07-17-extension-passkey-detach-for-web-prf-design.md.
 */

/** `postMessage` payload `source` tags — disambiguate our messages from any
 *  other page/extension traffic and set the direction of a message. */
export const HBS_WEB_MESSAGE_SOURCE = 'hbs-web' as const;
export const HBS_EXT_MESSAGE_SOURCE = 'hbs-ext' as const;

/** Web → extension: detach the passkey provider for the coming PRF ceremony. */
export const HBS_SUSPEND_PASSKEY_PROVIDER = 'HBS_SUSPEND_PASSKEY_PROVIDER' as const;
/** Extension → web: the provider is detached (or was never attached). */
export const HBS_PASSKEY_PROVIDER_SUSPENDED = 'HBS_PASSKEY_PROVIDER_SUSPENDED' as const;
/** Web → extension: the ceremony is done; re-attach the provider. */
export const HBS_RESUME_PASSKEY_PROVIDER = 'HBS_RESUME_PASSKEY_PROVIDER' as const;
/** Extension → web: the provider has been re-attached (or nothing to do). */
export const HBS_PASSKEY_PROVIDER_RESUMED = 'HBS_PASSKEY_PROVIDER_RESUMED' as const;

/** A message the web app posts to the content-script relay. */
export interface HbsWebHandshakeMessage {
  source: typeof HBS_WEB_MESSAGE_SOURCE;
  type: typeof HBS_SUSPEND_PASSKEY_PROVIDER | typeof HBS_RESUME_PASSKEY_PROVIDER;
}

/** A message the content-script relay posts back to the web app. */
export interface HbsExtHandshakeMessage {
  source: typeof HBS_EXT_MESSAGE_SOURCE;
  type: typeof HBS_PASSKEY_PROVIDER_SUSPENDED | typeof HBS_PASSKEY_PROVIDER_RESUMED;
  /** Whether the provider was actually attached and got detached — only the
   *  SUSPENDED reply carries it. `false` means no detach happened (provider off
   *  / unavailable), so the web app skips the resume. */
  wasAttached?: boolean;
}
